import { useContext, useEffect, useState } from 'react'
import { __ } from '@wordpress/i18n'
import Button from '../Common/Button';
import Source from '../Common/Source';
import AddSourceModal from './AddSourceModal';
import SbUtils from '../../Utils/SbUtils';
import FreeRetrieverModal from './FreeRetrieverModal';
import {
    getSourceBadge,
    getSourceProducts,
    getSourceCategories,
    getSourceTags
} from '../../Utils/WooCommerceSourceUtils';

const CustomizerSourcesModal = ( props ) => {
    const {
		sources,
        sourcesCount,
		editorNotification,
		sbCustomizer,
		editorTopLoader,
        addSModal,
        freeRetModal,
        apis,
        freeRet
	} = useContext( SbUtils.getCurrentContext() );

    /**
     * Normalize selected sources to database IDs for Review Alerts
     * This handles backward compatibility with old account_id format
     */
    const normalizeSelectedSources = () => {
        const selected = Array.isArray( props.selectedSources ) ? props.selectedSources : [];

        // If not using database IDs, return as-is (regular feeds)
        if ( !props.useDbId ) {
            return selected;
        }

        // For Review Alerts, convert any account_id strings to database IDs
        // Look up by both account_id and id to handle numeric account_ids (e.g., Facebook)
        const sourcesList = sources?.sourcesList || [];
        return selected.map( selectedId => {
            const selectedStr = String( selectedId );

            // Find the source that matches either the account_id or the database id
            const matchingSource = sourcesList.find(
                s => String( s.account_id ) === selectedStr || String( s.id ) === selectedStr
            );

            return matchingSource ? matchingSource.id : null;
        } ).filter( id => id !== null );
    };

    // Ensure choosedSources is always an array of normalized IDs
    const [ choosedSources, setChoosedSources ] = useState( normalizeSelectedSources );
    const [ addSourcesModalActive, setAddSourcesModalActive ] = useState( false );
    const [ isLoadingSources, setIsLoadingSources ] = useState( false );
    const [ sourcesPage, setSourcesPage ] = useState(2);

    /**
     * Fetch/refresh sources list from the server
     * Called after adding a new source to refresh the list
     */
    const fetchSources = () => {
        setIsLoadingSources( true );
        const formData = {
            action : 'sbr_feed_saver_manager_load_more_sources',
            sources_page : 1
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( resp ) => {
                setIsLoadingSources( false );
                if ( resp?.sourcesList !== undefined ) {
                    sources.setSourcesList( resp?.sourcesList );
                    sourcesCount.setSourcesNumber( resp?.sourcesCount );
                    // Reset pagination since we reloaded from page 1
                    setSourcesPage( 2 );
                }
            },
            editorTopLoader,
            null,
            null
        );
    };

    /**
     * Get the appropriate source identifier based on context
     * Review Alerts use database id to avoid URL encoding issues (PR #418)
     * Regular feeds use account_id for backward compatibility
     */
    const getSourceIdentifier = ( source ) => {
        return props.useDbId ? source.id : source.account_id;
    };

    /**
     * Check if a source is currently selected
     * For Review Alerts (useDbId), we only check by database ID since sources are normalized
     * For regular feeds, we check by account_id
     */
    const isSourceSelected = ( source ) => {
        const identifier = getSourceIdentifier( source );
        return choosedSources.some( s => String( s ) === String( identifier ) );
    };

    const selectSourceClick = ( source ) => {
        // Create a copy of the current selection - ensure it's an array
        const sSources = Array.isArray( choosedSources ) ? [ ...choosedSources ] : [];
        const sourceId = getSourceIdentifier( source );

        // Check if source is already selected using the appropriate identifier
        const existingIndex = sSources.findIndex( s => String( s ) === String( sourceId ) );

        if ( existingIndex === -1 ) {
            // Not selected, add it
            sSources.push( sourceId );
        } else {
            // Already selected, remove it
            sSources.splice( existingIndex, 1 );
        }
        setChoosedSources( sSources );
    }

    // Handle Facebook modal activation
    useEffect( () => {
        if( props.fbModal.connectFacebookActive === true ){
            setAddSourcesModalActive( true )
        }
    }, [ props.fbModal.connectFacebookActive ] );

    const loadMoreSource = () => {
        setIsLoadingSources(true)
        //Ajax Call to get Template Settings
        const formData = {
            action : 'sbr_feed_saver_manager_load_more_sources',
            sources_page : sourcesPage
        },
        notificationsContent = {
            success : {
                icon : 'success',
                text : __( 'New sources loaded', 'sb-customizer' )
            }
        }

            SbUtils.ajaxPost(
                sbCustomizer.ajaxHandler,
                formData,
                ( resp ) => { //Call Back Function
                    setIsLoadingSources(false)
                    if( resp?.sourcesList !== undefined && resp?.sourcesList.length > 0 ){
                        const newSourcesList = sources?.sourcesList.concat(resp?.sourcesList);
                        sources.setSourcesList(newSourcesList);
                        sourcesCount.setSourcesNumber( resp?.sourcesCount );
                        setSourcesPage(sourcesPage + 1)
                    }
                },
                editorTopLoader,
                editorNotification,
                notificationsContent
        )
    }

    const [onSuccessApiKey, setOnSuccessApiKey] = useState(null)
    // Initialize modal screens when context values are available
    useEffect(() => {
        if (sbCustomizer?.openSourceModal) {
            setAddSourcesModalActive( true )
        }
        const initModalScreen = SbUtils.initAddSourceModalScreen(apis, freeRet)  // addSource, freeRetriever
        addSModal.setModalType(initModalScreen)

        const initRetrModal = SbUtils.initRetrieveModalScreen(apis, freeRet) // verifyEmail - sourceAdded - limitExceeded - addApiKey
        freeRetModal.setRetModalType(initRetrModal)
    }, [ sbCustomizer?.openSourceModal, apis, freeRet, addSModal, freeRetModal ]);

    return (
        <>
        {
            !addSourcesModalActive &&
            <div className='sb-feedsources-modal sb-fs'>
                <div className='sb-feedsources-modal-content'>
                    <div className='sb-feedsources-modal-heading sb-fs'>
                        <h4 className='sb-h4'>{ props.title || __( 'Add Sources', 'sb-customizer' ) }</h4>
                        <span className='sb-text-small sb-dark2-text'>{ props.description || __( 'Select one or more sources you would like to add', 'sb-customizer' ) }</span>
                    </div>
                    <div className='sb-feedsources-modal-list sb-fs'>
                        <Button
                            text={ __( 'Add New', 'sb-customizer' ) }
                            icon='plus'
                            customClass='sb-addnew-source'
                            boxshadow={ false }
                            size='small'
                            iconSize='14'
                            onClick={ () => {
                                setAddSourcesModalActive( true )
                            } }
                        />
                    {
                        sources.sourcesList.map( ( source, sourceInd ) => {
                            return (
                                <Source
                                    key={ sourceInd }
                                    provider={ source.provider }
                                    checkbox={true}
                                    isChecked={ isSourceSelected( source ) }
                                    name={ source.name }
                                    info={ source.info }
                                    badge={ getSourceBadge( source ) }
                                    productsInfo={ getSourceProducts( source ) }
                                    categoriesInfo={ getSourceCategories( source ) }
                                    tagsInfo={ getSourceTags( source ) }
                                    onClick={ () => {
                                        selectSourceClick( source );
                                    }}
                                />
                            )
                        } )
                    }
                    </div>
                    {
                    //Should Show Load More Sources Button
                    <div className='sb-load-source-bottom sb-fs'>
                        <div className='sb-load-source-number sb-fs'>{__('Showing', 'reviews-feed') + ' ' + sources.sourcesList.length + ' ' + __('of', 'reviews-feed') + ' ' + sourcesCount.sourcesNumber + ' ' + __('Sources', 'reviews-feed')}</div>

                        {
                            sourcesCount.sourcesNumber > sources.sourcesList.length &&
                            <Button
                                full-width='true'
                                type='secondary'
                                size='medium'
                                icon={isLoadingSources ? 'loader' : 'loadbutton'}
                                loading={isLoadingSources}
                                text={__('Load More', 'reviews-feed')}
                                onClick={() => {
                                    loadMoreSource()
                                }}
                            />
                        }
                    </div>
                }

                    <div className='sb-feedsources-modal-btns sb-fs'>
                        <Button
                                size='medium'
                                type='secondary'
                                onClick={ () => {
                                    props?.onCancel &&
                                        props.onCancel()
                                }}
                                text={ __( 'Cancel', 'sb-customizer' ) }
                            />
                            <Button
                                icon={ props.buttonIcon || 'plus' }
                                iconSize='12'
                                iconPosition={ props.buttonIconPosition || 'left' }
                                size='medium'
                                type='primary'
                                onClick={ () => {
                                    props.onUpdateSources( choosedSources )
                                } }
                                text={ props.buttonText || __( 'Add', 'sb-customizer' ) }
                            />
                    </div>
                </div>
            </div>
        }
        {
            addSourcesModalActive &&
            <>
                {
                    addSModal?.modalType === 'addSource' &&
                    <AddSourceModal
                        openDialog={ () => {
                            setAddSourcesModalActive( true )
                        } }
                        onCancel={ () => {
                            setAddSourcesModalActive( false )
                        } }
                        onSuccess={ () => {
                            // Refresh sources list after adding a new source
                            fetchSources();
                        } }
                        onSuccessApiKey={onSuccessApiKey}
                    />
                }
              {
                addSModal?.modalType === 'freeRetriever' &&
                    <FreeRetrieverModal
                        screenType={freeRetModal?.retModalType} // - verifyEmail - sourceAdded - limitExceeded - addApiKey
                        openDialog={ () => {
                            setAddSourcesModalActive( true )
                        } }
                        onCancel={ () => {
                            setAddSourcesModalActive( false )
                            setOnSuccessApiKey(null)
                        } }
                        onSuccessApiKey={(provider) => {
                            setOnSuccessApiKey(provider)
                        }}
                    />
                }
            </>
        }

    </>

    )
}


export default CustomizerSourcesModal;